import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const ease = [0.76, 0, 0.24, 1];

const MaskLine = ({ children, delay = 0 }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden flex flex-col justify-end"
    >
      {/* Parallax background */}
      <motion.div style={{ y: yBg, scale }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1541537103745-ea3429c65dc4?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Cinematic neon cityscape"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-[#050505]" />
      </motion.div>

      {/* Spotlight glow */}
      <div className="pointer-events-none absolute top-1/4 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-[#e1ff00]/10 blur-[140px] z-0" />

      {/* Overline top */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute top-28 md:top-32 left-0 right-0 z-10"
      >
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 flex justify-between text-[#e1ff00] font-outfit text-xs uppercase tracking-[0.3em]">
          <span>Real-time Cinematics</span>
        </div>
      </motion.div>

      {/* Main headline */}
      <motion.div
        style={{ y: yText, opacity }}
        className="max-w-screen-2xl mx-auto w-full px-6 md:px-12 pb-16 md:pb-24 relative z-10"
      >
        <div className="flex items-end justify-between flex-wrap gap-6">
          <h1 className="font-bebas uppercase leading-[0.8] tracking-tight text-white">
            <MaskLine delay={0.2}>
              <span className="block text-[22vw] md:text-[13vw]">Asraf Noor</span>
            </MaskLine>
            <MaskLine delay={0.4}>
              <span className="block text-[9vw] md:text-[7vw] text-neutral-400">
                Unreal Engine <span className="text-[#e1ff00]">Artist</span>
              </span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 1, ease }}
            className="font-manrope text-neutral-300 max-w-xs text-base md:text-lg font-light leading-relaxed"
          >
            I direct short cinematic films inside game engines, moody, weird, and rendered in
            real time.
          </motion.p>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
      >
        <span className="font-outfit text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          Scroll
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          className="h-8 w-px bg-neutral-600"
        />
      </motion.div>
    </section>
  );
};
